import {
  brewDayUnit,
  type Dimension,
  dec,
  fromCanonical,
  toCanonical,
  UNITS,
  type UnitId,
} from "@bl/domain";
import type { BrewDetail } from "./api";

/**
 * What a brew's pulls come to, lot by lot, worked out as the brewer types (PLAN.md, "Slice 2"). A brew
 * can use stock inventory does not have, and Brewed is never held for it (Scott, 2026-10-02): a pull
 * with no lot, or what a lot is asked for beyond what it holds, is kept on the brew to be given a lot
 * later. Falling short of the recipe is only a warning. Amounts are typed in each line's own unit and
 * compared in canonical units.
 */

export type BrewLine = BrewDetail["lines"][number];
export type Lot = BrewLine["options"][number];
export type Pull = BrewLine["pulls"][number];

export interface DraftPull {
  key: string;
  /** `lotId|locationId`, or `NONE`. */
  lotKey: string;
  /** As typed, in its lot's unit (`unitOfPull`). */
  amount: string;
}

/** The lot of a pull that no lot covers: stock not in inventory, to be given its lot later. */
export const NONE = "none";

export const lotKey = (o: { lotId: string | null; locationId: string | null }) =>
  o.lotId === null ? NONE : `${o.lotId}|${o.locationId}`;

let pullKeys = 0;
export const newPullKey = () => `new-${++pullKeys}`;

/** How a line's amounts read: grain in pounds, hops in ounces, anything else in its item's unit. */
export function unitOf(line: BrewLine): UnitId {
  return line.item ? brewDayUnit(line.item, line.kind === "hop") : "g";
}

const factorOf = (line: BrewLine) => Number(UNITS[unitOf(line)].factor);

/**
 * Whether a lot is of another item than the line names: a yeast's harvested slurry, offered in place of
 * the pitch the recipe asks for (Tyler, 2026-10-07). It is weighed, where the pitch is not.
 */
export const repitches = (line: BrewLine, lot: { itemId: string } | undefined) =>
  lot !== undefined && line.item !== null && lot.itemId !== line.item.id;

/** How a pull's amount reads: in its lot's unit, or the line's for what no lot covers. */
export function unitOfPull(line: BrewLine, key: string): UnitId {
  return lotsOf(line).find((o) => lotKey(o) === key)?.unit ?? unitOf(line);
}

/** A number to at most `places` decimals, without trailing zeros: 12.5, not 12.500. */
export const shown = (value: number, places = 3) => String(Number(value.toFixed(places)));

/** A canonical quantity in a unit, to three places. */
export const inUnit = (canonical: string, unit: UnitId) =>
  shown(Number(fromCanonical(canonical, unit).toFixed(6)));

/** Digits and one point, which is all the decimal keypad can type, kept that way when pasted into. */
export const decimalOnly = (value: string) =>
  value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1");

/**
 * Brew day's temperatures are kept in °C and typed in °F, as White Oak works (`recipeFormat.ts`), both
 * exact to six places.
 */
export const celsiusOf = (fahrenheit: string) =>
  dec(fahrenheit).minus(dec("32")).times(dec("5")).div(dec("9")).toFixed(6);
export const fahrenheitOf = (celsius: string) =>
  dec(celsius).times(dec("9")).div(dec("5")).plus(dec("32")).toFixed(6);

/** What a typed amount comes to, with nothing typed or a lone point as zero. */
export const amountOf = (value: string) => Number(value) || 0;

const hasLot = (p: Pull): p is Pull & { lotId: string; locationId: string } => p.lotId !== null;

/** Every lot a line could take from, and any already chosen that has since run out there. */
export function lotsOf(line: BrewLine): Lot[] {
  return [
    ...line.options,
    ...line.pulls.filter(hasLot).filter((p) => !line.options.some((o) => lotKey(o) === lotKey(p))),
  ];
}

/** What a lot holds, in a unit, as its field shows it. */
export const holdsIn = (lot: Lot, unit: UnitId) => Number(inUnit(lot.onHand, unit));

/**
 * What assigning an amount typed in a unit sends, canonical. An amount that shows as all that is still
 * to assign, or as all the lot holds, is sent as exactly that, so rounding to what a field shows never
 * leaves a sliver to assign behind.
 */
export function assignedQuantity(
  pull: Pull,
  lot: Lot,
  typed: number,
  unit: UnitId,
  dimension: Dimension,
): string {
  const slack = 0.0005;
  const lotHasAll = dec(lot.onHand).gte(dec(pull.quantity));
  if (lotHasAll && typed >= Number(inUnit(pull.quantity, unit)) - slack) return pull.quantity;
  if (!lotHasAll && typed >= holdsIn(lot, unit) - slack) return lot.onHand;
  return toCanonical({ value: String(typed), unit }, dimension);
}

/** What a line takes that is not in inventory, in its own unit, by what is typed now. */
export function notInInventoryOf(pulls: DraftPull[]): number {
  return pulls.reduce((n, p) => n + (p.lotKey === NONE ? amountOf(p.amount) : 0), 0);
}

/**
 * What a line still lacks, in its own unit, by what is typed now. A yeast repitched from its strain's
 * harvest lacks nothing: the harvest stands in for the pitch, in a measure the pitch is not.
 */
export function shortOf(line: BrewLine, pulls: DraftPull[]): number {
  if (line.needed === null) return 0;
  const lots = lotsOf(line);
  const of = (p: DraftPull) => lots.find((o) => lotKey(o) === p.lotKey);
  if (pulls.some((p) => amountOf(p.amount) > 0 && repitches(line, of(p)))) return 0;
  const needed = Number(inUnit(line.needed, unitOf(line)));
  const taken = pulls.reduce((n, p) => n + (p.lotKey ? amountOf(p.amount) : 0), 0);
  return needed - taken > 0.0005 ? needed - taken : 0;
}

export interface LotStanding {
  /** What the lot holds there now, canonical. */
  holds: number;
  /** What the brew's lines take from it between them, canonical. */
  taken: number;
  /** The lines taking from it. */
  lineIds: Set<string>;
  /** Half the last digit a field shows, canonical: anything under it is rounding, not a shortfall. */
  slack: number;
}

/**
 * Each lot against what the brew's lines take from it between them. Two lines of the same hop can each
 * fit a lot and still, together, ask it for more than it holds.
 */
export function lotStanding(
  lines: BrewLine[],
  pulls: Record<string, DraftPull[]>,
): Map<string, LotStanding> {
  const out = new Map<string, LotStanding>();
  const at = (key: string) => {
    let s = out.get(key);
    if (!s) {
      s = { holds: 0, taken: 0, lineIds: new Set(), slack: 0 };
      out.set(key, s);
    }
    return s;
  };
  for (const line of lines) {
    if (!line.item) continue;
    for (const lot of lotsOf(line)) at(lotKey(lot)).holds = Number(lot.onHand);
    for (const p of pulls[line.recipeLineId] ?? []) {
      if (!p.lotKey || p.lotKey === NONE || amountOf(p.amount) <= 0) continue;
      const factor = Number(UNITS[unitOfPull(line, p.lotKey)].factor);
      const s = at(p.lotKey);
      s.taken += amountOf(p.amount) * factor;
      s.lineIds.add(line.recipeLineId);
      s.slack = Math.max(s.slack, 0.0005 * factor);
    }
  }
  return out;
}

/** How much more than it holds a lot is asked for, canonical, or 0. */
export function overBy(standing: LotStanding | undefined): number {
  if (!standing) return 0;
  const over = standing.taken - standing.holds;
  return over > standing.slack ? over : 0;
}

/**
 * An item's lines planned again, oldest lot first, against what its lots hold now: the plan a brew
 * starts with, made again after the stock has moved, with what no lot covers as not in inventory. Lines
 * of the item that ask no set amount keep their pulls, and what those take is left out of what the
 * others can have. Each amount is rounded to what the field shows, which is within the slack `overBy`
 * allows.
 */
export function replanned(
  lines: BrewLine[],
  itemId: string,
  pulls: Record<string, DraftPull[]>,
): Record<string, DraftPull[]> {
  const mine = lines.filter((l) => l.item?.id === itemId);
  // The item's own lots: a strain's harvest is the brewer's to choose, in its own measure.
  const lots = (mine[0]?.options ?? []).filter((o) => o.itemId === itemId);
  const left = new Map(lots.map((o) => [lotKey(o), Number(o.onHand)]));
  for (const l of mine.filter((x) => x.needed === null)) {
    for (const p of pulls[l.recipeLineId] ?? []) {
      if (p.lotKey === NONE) continue;
      const factor = Number(UNITS[unitOfPull(l, p.lotKey)].factor);
      left.set(p.lotKey, (left.get(p.lotKey) ?? 0) - amountOf(p.amount) * factor);
    }
  }
  const out: Record<string, DraftPull[]> = {};
  for (const l of mine) {
    if (l.needed === null) continue;
    const factor = factorOf(l);
    let need = Number(l.needed);
    const next: DraftPull[] = [];
    for (const o of lots) {
      if (need <= 0) break;
      const key = lotKey(o);
      const have = left.get(key) ?? 0;
      if (have <= 0) continue;
      const amount = Math.round((Math.min(have, need) / factor) * 1000) / 1000;
      if (amount <= 0) continue;
      next.push({ key: newPullKey(), lotKey: key, amount: String(amount) });
      left.set(key, have - amount * factor);
      need -= amount * factor;
    }
    const rest = Math.round((need / factor) * 1000) / 1000;
    if (rest > 0) next.push({ key: newPullKey(), lotKey: NONE, amount: String(rest) });
    out[l.recipeLineId] = next;
  }
  return out;
}
